import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePersistedState } from "@/lib/usePersistedState";
import PlayerAvatar from "@/components/PlayerAvatar";
import { FavoriteStar } from "@/components/FavoriteStar";
import { PushToggle } from "@/lib/push";
import { trFoot, trStyle, trPositionFull, trPositionShort } from "@/lib/pesTranslations";
import { sanitizeDecimalInput } from "@/lib/amountInput";

function fmtEur(n) {
    if (!n) return "—";
    if (n >= 1_000_000) return `€${(n / 1_000_000).toFixed(2)}mln`;
    if (n >= 1_000) return `€${(n / 1_000).toFixed(0)}k`;
    return `€${n}`;
}

// Formato compatto per gli orari delle buste: "gg/mm HH:MM" (Europe/Rome).
function fmtShort(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            timeZone: "Europe/Rome",
            day: "2-digit", month: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

// Elenco buste in ordine cronologico (chi ha offerto prima è in cima).
function BidsTimeline({ bids, winnerTeam }) {
    if (!bids || bids.length === 0) return null;
    const ordered = [...bids].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    return (
        <details className="mt-2" data-testid="bids-timeline">
            <summary className="cursor-pointer op-mono text-[11px] text-t8f95a3 hover:text-tc9ccd3">
                {ordered.length} buste — ordine e orari
            </summary>
            <div className="mt-2 space-y-1 pl-3 border-l border-t22252a">
                {ordered.map((b, i) => (
                    <div key={i} className="flex items-center justify-between op-mono text-[11px]">
                        <span className={b.team_name === winnerTeam ? "text-te8ff00" : "text-tc9ccd3"}>
                            <span className="text-t5c6370 mr-1">{i + 1}.</span>
                            {b.team_name}
                            {b.team_name === winnerTeam && <span className="ml-1 text-[9px] text-te8ff00">✓ vincitore</span>}
                        </span>
                        <span className="text-t8f95a3">
                            <span className="text-t5c6370 mr-2">{fmtShort(b.created_at)}</span>
                            {fmtEur(b.amount)}
                        </span>
                    </div>
                ))}
            </div>
        </details>
    );
}

// Link a Transfermarkt: usa il profilo salvato (tm_link) se c'è, altrimenti ricerca per nome.
function tmUrl(player) {
    if (player?.tm_link) return player.tm_link;
    const q = encodeURIComponent(player?.name || "");
    return `https://www.transfermarkt.it/schnellsuche/ergebnis/schnellsuche?query=${q}`;
}

function FaSelect({ value, onChange, placeholder, options, testId, wide, translate }) {
    const trans = translate || ((v) => v);
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`op-input ${wide ? "min-w-[170px]" : "min-w-[120px]"}`}
            data-testid={testId}
        >
            <option value="">{placeholder}</option>
            {(options || []).map((o) => (
                <option key={o} value={o}>{trans(o)}</option>
            ))}
        </select>
    );
}

function FaRange({ label, minVal, maxVal, onMin, onMax, testId }) {
    return (
        <div>
            <div className="op-label text-[9px] mb-1 text-t8f95a3">{label}</div>
            <div className="flex items-center gap-1">
                <input type="number" value={minVal} onChange={(e) => onMin(e.target.value)} placeholder="min"
                    className="op-input op-mono py-1 text-xs w-full" data-testid={`${testId}-min`} />
                <span className="op-label text-t5c6370">→</span>
                <input type="number" value={maxVal} onChange={(e) => onMax(e.target.value)} placeholder="max"
                    className="op-input op-mono py-1 text-xs w-full" data-testid={`${testId}-max`} />
            </div>
        </div>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            timeZone: "Europe/Rome",
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function timeLeft(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "Scaduta";
    const h = Math.floor(ms / 3600_000);
    const m = Math.floor((ms % 3600_000) / 60_000);
    if (h >= 24) return `${Math.floor(h / 24)}g ${h % 24}h`;
    return `${h}h ${m}m`;
}

const STATUS_LABELS = {
    open: "Aperta",
    closed: "Chiusa",
    tie_resolution: "Risoluzione pareggi",
    executed: "Eseguita",
};

const STATUS_COLORS = {
    open: "rgb(var(--tc-2bb6ff))",
    closed: "rgb(var(--tc-8f95a3))",
    tie_resolution: "rgb(var(--tc-ff7a1a))",
    executed: "rgb(var(--tc-e8ff00))",
};

export default function Svincolati({ embedded = false }) {
    const { user } = useAuth();
    const [auctions, setAuctions] = useState([]);
    // ID dell'asta selezionata persistito: tornando indietro da una scheda
    // giocatore si resta sull'asta che si stava guardando.
    const [selectedId, setSelectedId] = usePersistedState("svincolati.selectedId", null);
    const [loading, setLoading] = useState(true);

    const selected = useMemo(
        () => auctions.find((a) => a.id === selectedId) || null,
        [auctions, selectedId],
    );

    const refresh = async () => {
        try {
            const { data } = await api.get("/auctions");
            setAuctions(data || []);
            // Auto-seleziona la prima asta aperta solo se non c'è già una scelta valida.
            const hasValid = (data || []).some((a) => a.id === selectedId);
            if (!hasValid) {
                const open = (data || []).find((a) => a.status === "open");
                setSelectedId(open ? open.id : null);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    /* eslint-disable */
    useEffect(() => {
        refresh();
    }, []);
    /* eslint-enable */

    const content = (
        <>
            {!embedded && (
                <>
                    <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// FREE AGENTS</div>
                    <h1 className="op-heading text-5xl sm:text-6xl mt-3">
                        <span className="op-gradient-text">SVINCOLATI</span>
                    </h1>
                </>
            )}
            <p className="text-t8f95a3 text-sm mt-3 max-w-2xl">
                Asta a busta chiusa. Tutti i giocatori senza squadra sono disponibili.
                Prezzo minimo = valore di mercato. Le buste si aprono tutte insieme alla scadenza.
            </p>

            {!embedded && user && user.team_id && (
                <div className="mt-5">
                    <PushToggle />
                </div>
            )}

            {/* Auctions list */}
            <section className="mt-8" data-testid="auctions-list">
                <div className="op-label mb-3">// SESSIONI</div>
                {loading ? (
                    <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>
                ) : auctions.length === 0 ? (
                    <div className="op-card p-5 text-t8f95a3 text-sm">
                        Nessuna asta svincolati attiva. L&apos;admin aprirà una sessione quando deciso.
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {auctions.map((a) => {
                            const isSel = selected?.id === a.id;
                            const color = STATUS_COLORS[a.status] || "rgb(var(--tc-8f95a3))";
                            return (
                                <button
                                    key={a.id}
                                    onClick={() => setSelectedId(a.id)}
                                    className="op-card p-4 text-left transition-colors hover:border-t2bb6ff"
                                    style={{
                                        borderLeft: `3px solid ${color}`,
                                        background: isSel ? "rgb(var(--tc-15171c))" : undefined,
                                        outline: isSel ? `1px solid ${color}` : "none",
                                    }}
                                    data-testid={`auction-card-${a.id}`}
                                >
                                    <div className="op-label" style={{ color }}>
                                        {STATUS_LABELS[a.status] || a.status} · {a.season}
                                    </div>
                                    <div className="op-heading text-xl mt-2">{a.name}</div>
                                    <div className="op-mono text-xs text-t8f95a3 mt-2">
                                        Scadenza: {fmtDate(a.end_date)}
                                    </div>
                                    {a.status === "open" && (
                                        <div className="op-mono text-xs mt-1" style={{ color }}>
                                            ⏱ {timeLeft(a.end_date)}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Selected auction details */}
            {selected && (
                <AuctionPanel
                    auction={selected}
                    onRefresh={refresh}
                    isPresident={!!(user && user.team_id)}
                />
            )}
        </>
    );

    if (embedded) {
        return <div data-testid="svincolati-panel">{content}</div>;
    }
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="svincolati-page">
            {content}
        </div>
    );
}

function AuctionPanel({ auction, onRefresh, isPresident }) {
    if (auction.status === "executed" || auction.status === "tie_resolution") {
        return <AuctionResults auction={auction} />;
    }
    if (auction.status === "open" && isPresident) {
        return <BiddingPanel auction={auction} onRefresh={onRefresh} />;
    }
    if (auction.status === "open" && !isPresident) {
        return (
            <div className="op-card p-6 mt-8" data-testid="not-president">
                <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// SOLO PRESIDENTI</div>
                <p className="text-sm text-tc9ccd3 mt-2">
                    Per partecipare devi essere assegnato a una squadra.
                </p>
            </div>
        );
    }
    return null;
}

const EMPTY_FILTERS = {
    q: "", position: "", playing_style: "", foot: "", nationality: "",
    overall_min: "", overall_max: "", age_min: "", age_max: "",
    height_min: "", height_max: "", weight_min: "", weight_max: "",
    wage_min: "", wage_max: "", sort: "overall", order: "desc",
};

function BiddingPanel({ auction, onRefresh }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    // Filtri e pagina persistiti PER ASTA: tornando indietro da una scheda
    // giocatore il filtro impostato (ruolo, età, abilità…) e la pagina restano.
    const [page, setPage] = usePersistedState(`fa.page.${auction.id}`, 1);
    const [filters, setFilters] = usePersistedState(`fa.filters.${auction.id}`, EMPTY_FILTERS);
    const [opts, setOpts] = useState({});
    const [myBids, setMyBids] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [bidTarget, setBidTarget] = useState(null);

    const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
    const activeCount = Object.entries(filters).filter(
        ([k, v]) => v && k !== "sort" && k !== "order"
    ).length;

    const myBidsByPid = useMemo(() => {
        const m = {};
        for (const b of myBids) m[b.player_pes_id] = b;
        return m;
    }, [myBids]);

    const fetchFreeAgents = async () => {
        setLoadingList(true);
        try {
            const params = new URLSearchParams();
            for (const [k, v] of Object.entries(filters)) {
                if (v !== "" && v != null) params.set(k, v);
            }
            params.set("page", page);
            params.set("page_size", "30");
            const { data } = await api.get(`/auctions/${auction.id}/free-agents?${params}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingList(false);
        }
    };

    const fetchMyBids = async () => {
        try {
            const { data } = await api.get(`/auctions/${auction.id}/my-bids`);
            setMyBids(data || []);
        } catch (e) {/* ignore */}
    };

    /* eslint-disable */
    useEffect(() => {
        fetchFreeAgents();
    }, [auction.id, filters, page]);
    /* eslint-enable */

    /* eslint-disable */
    useEffect(() => {
        fetchMyBids();
        api.get("/players/filters").then((r) => setOpts(r.data || {})).catch(() => {});
    }, [auction.id]);
    /* eslint-enable */

    const onBidSent = () => {
        setBidTarget(null);
        fetchMyBids();
    };

    return (
        <section className="mt-10" data-testid="bidding-panel">
            <div className="op-card p-5 mb-6" style={{ borderLeft: auction.is_ugly_auction ? "3px solid rgb(var(--tc-e8ff00))" : undefined }}>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="op-label" style={{ color: auction.is_ugly_auction ? "rgb(var(--tc-e8ff00))" : "rgb(var(--tc-ff7a1a))" }}>
                        // IN CORSO — {auction.name}
                    </div>
                    {auction.is_ugly_auction && (
                        <span className="op-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border border-te8ff00 text-te8ff00">
                            SETTORE UGLY · solo under 22
                        </span>
                    )}
                </div>
                <div className="op-heading text-2xl mt-2">Scadenza: {fmtDate(auction.end_date)}</div>
                <div className="op-mono text-sm text-t2bb6ff mt-1">⏱ {timeLeft(auction.end_date)}</div>
                <p className="text-xs text-t8f95a3 mt-3 leading-relaxed">
                    Invia un&apos;offerta in busta chiusa. Nessuno vede le offerte degli altri fino all&apos;apertura.
                    <span className="text-tff7a1a"> Le offerte sono definitive: una volta inviate non possono essere modificate o ritirate.</span>
                    {auction.is_ugly_auction && (
                        <span className="text-te8ff00"> · I giocatori acquistati verranno inseriti nel settore Ugly.</span>
                    )}
                </p>
            </div>

            {/* My bids summary */}
            <div className="mb-6" data-testid="my-bids-section">
                <div className="op-label mb-3 text-t2bb6ff">// LE MIE OFFERTE ({myBids.length})</div>
                {myBids.length === 0 ? (
                    <div className="op-card p-4 text-t8f95a3 text-sm">
                        Non hai ancora inviato offerte.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {myBids.map((b) => (
                            <MyBidRow key={b.player_pes_id} bid={b} />
                        ))}
                    </div>
                )}
            </div>

            {/* Offerta diretta su giocatore Transfermarkt (chi non ha la scheda PESDB):
                disponibile sia nelle aste ugly sia negli svincolati normali */}
            <TmIdBidCard auction={auction} onSent={fetchMyBids} />

            {/* Filters */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="op-label">// GIOCATORI DISPONIBILI ({total})</div>
                {activeCount > 0 && (
                    <button
                        className="op-btn op-btn-ghost text-xs"
                        onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}
                        data-testid="clear-filters-btn"
                    >
                        Pulisci filtri ({activeCount})
                    </button>
                )}
            </div>
            <div className="op-card mb-4" data-testid="bidding-filters">
                <div className="p-3 flex flex-wrap items-center gap-2">
                    <input
                        className="op-input flex-1 min-w-[200px]"
                        placeholder="Cerca per nome…"
                        value={filters.q}
                        onChange={(e) => setF("q", e.target.value)}
                        data-testid="search-input"
                    />
                    <FaSelect value={filters.position} onChange={(v) => setF("position", v)} placeholder="Ruolo"
                        options={opts.positions} translate={(v) => `${trPositionShort(v)} · ${trPositionFull(v)}`} testId="position-filter" />
                    <FaSelect value={filters.playing_style} onChange={(v) => setF("playing_style", v)} placeholder="Stile di gioco"
                        options={opts.playing_styles} translate={trStyle} testId="style-filter" wide />
                    <FaSelect value={filters.foot} onChange={(v) => setF("foot", v)} placeholder="Piede"
                        options={opts.foots} translate={trFoot} testId="foot-filter" />
                    <FaSelect value={filters.nationality} onChange={(v) => setF("nationality", v)} placeholder="Nazione"
                        options={opts.nationalities} testId="nationality-filter" wide />
                    <select
                        className="op-input"
                        value={`${filters.sort}:${filters.order}`}
                        onChange={(e) => {
                            const [s, o] = e.target.value.split(":");
                            setFilters((f) => ({ ...f, sort: s, order: o }));
                            setPage(1);
                        }}
                        data-testid="sort-filter"
                    >
                        <option value="overall:desc">VG ↓</option>
                        <option value="overall:asc">VG ↑</option>
                        <option value="market_value:desc">Valore TM ↓</option>
                        <option value="market_value:asc">Valore TM ↑</option>
                        <option value="purchase_wage:desc">Stipendio ↓</option>
                        <option value="purchase_wage:asc">Stipendio ↑</option>
                        <option value="name:asc">Nome A-Z</option>
                        <option value="age:asc">Età ↑</option>
                        <option value="age:desc">Età ↓</option>
                        <option value="height:desc">Altezza ↓</option>
                    </select>
                </div>
                <div className="border-t border-t22252a p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <FaRange label="VG" minVal={filters.overall_min} maxVal={filters.overall_max}
                        onMin={(v) => setF("overall_min", v)} onMax={(v) => setF("overall_max", v)} testId="overall" />
                    <FaRange label="Età" minVal={filters.age_min} maxVal={filters.age_max}
                        onMin={(v) => setF("age_min", v)} onMax={(v) => setF("age_max", v)} testId="age" />
                    <FaRange label="Altezza (cm)" minVal={filters.height_min} maxVal={filters.height_max}
                        onMin={(v) => setF("height_min", v)} onMax={(v) => setF("height_max", v)} testId="height" />
                    <FaRange label="Peso (kg)" minVal={filters.weight_min} maxVal={filters.weight_max}
                        onMin={(v) => setF("weight_min", v)} onMax={(v) => setF("weight_max", v)} testId="weight" />
                    <FaRange label="Stipendio (mln)" minVal={filters.wage_min} maxVal={filters.wage_max}
                        onMin={(v) => setF("wage_min", v)} onMax={(v) => setF("wage_max", v)} testId="wage" />
                </div>
            </div>

            {/* List */}
            {loadingList ? (
                <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>
            ) : (
                <div className="space-y-2">
                    {items.map((p) => (
                        <FreeAgentRow
                            key={p.pes_id}
                            player={p}
                            myBid={myBidsByPid[p.pes_id]}
                            onBid={() => setBidTarget(p)}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {total > 30 && (
                <div className="flex items-center justify-between mt-5">
                    <button
                        className="op-btn"
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        data-testid="prev-page"
                    >← Indietro</button>
                    <span className="op-mono text-xs text-t8f95a3">
                        Pagina {page} di {Math.ceil(total / 30)}
                    </span>
                    <button
                        className="op-btn"
                        disabled={page * 30 >= total}
                        onClick={() => setPage((p) => p + 1)}
                        data-testid="next-page"
                    >Avanti →</button>
                </div>
            )}

            {bidTarget && (
                <BidModal
                    auction={auction}
                    player={bidTarget}
                    onClose={() => setBidTarget(null)}
                    onDone={onBidSent}
                />
            )}
        </section>
    );
}

function vgColor(vg) {
    if (!vg) return "rgb(var(--tc-8f95a3))";
    if (vg >= 85) return "rgb(var(--tc-ff6b00))";
    if (vg >= 80) return "rgb(var(--tc-e8ff00))";
    if (vg >= 75) return "rgb(var(--tc-2bb6ff))";
    return "rgb(var(--tc-c9ccd3))";
}

function FreeAgentRow({ player, myBid, onBid }) {
    return (
        <div className="op-card p-3" data-testid={`fa-row-${player.pes_id}`}>
            {/* Riga 1: identità + VG */}
            <div className="flex items-center gap-2.5">
                <FavoriteStar pesId={player.pes_id} size={18} />
                <Link
                    to={`/giocatori/${player.pes_id}`}
                    className="flex items-center gap-2.5 flex-1 min-w-0 group"
                    data-testid={`fa-link-${player.pes_id}`}
                >
                    <PlayerAvatar pesImage={player.image_url} name={player.name} size={40} />
                    <div className="flex-1 min-w-0">
                        <div className="text-tffffff text-sm font-semibold leading-tight break-words group-hover:text-t2bb6ff transition-colors">
                            {player.name}
                        </div>
                        <div className="op-mono text-[11px] text-t8f95a3 truncate mt-0.5">
                            {trPositionShort(player.position)} · {player.age ?? "—"} anni · {player.nationality || "—"}
                        </div>
                    </div>
                </Link>
                <div className="text-center shrink-0 w-10">
                    <div className="op-mono font-bold text-lg leading-none" style={{ color: vgColor(player.overall) }}>
                        {player.overall ?? "—"}
                    </div>
                    <div className="op-label text-[8px] mt-0.5">VG</div>
                </div>
            </div>
            {/* Riga 2: prezzo TM + offri */}
            <div className="flex items-center justify-between gap-3 mt-2.5 pt-2.5 border-t border-t22252a">
                <div className="min-w-0 flex items-baseline gap-1.5 flex-wrap">
                    <span className="op-label text-[9px] text-tff7a1a shrink-0">Prezzo TM</span>
                    <span className="op-mono text-sm font-bold text-tffffff" data-testid={`fa-tm-${player.pes_id}`}>{fmtEur(player.market_value)}</span>
                    <a
                        href={tmUrl(player)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="op-mono text-[9px] text-t2bb6ff hover:underline shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`fa-tmlink-${player.pes_id}`}
                    >
                        Vedi su TM ↗
                    </a>
                </div>
                <button
                    className="op-btn op-mono text-xs shrink-0"
                    style={myBid ? { borderColor: "rgb(var(--tc-5c6370))", color: "rgb(var(--tc-5c6370))", cursor: "not-allowed" } : {}}
                    onClick={myBid ? undefined : onBid}
                    disabled={!!myBid}
                    title={myBid ? "Offerta già inviata (definitiva)" : undefined}
                    data-testid={`bid-btn-${player.pes_id}`}
                >
                    {myBid ? `✓ ${fmtEur(myBid.cash_amount)}` : "Offri"}
                </button>
            </div>
        </div>
    );
}

function TmIdBidCard({ auction, onSent }) {
    const [tmId, setTmId] = useState("");
    const [amount, setAmount] = useState("");
    const [years, setYears] = useState(2);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const id = parseInt(tmId, 10);
        const amt = Math.round(parseFloat(amount) * 1_000_000);
        if (!id || id <= 0) { alert("Inserisci un ID Transfermarkt valido (solo numero)."); return; }
        if (!amt || amt <= 0) { alert("Inserisci un importo offerta valido (in milioni)."); return; }
        if (!window.confirm(
            `Confermi l'offerta sul giocatore Transfermarkt #${id}?\n\n` +
            `Importo: ${(amt / 1e6).toFixed(1)}M · Contratto: ${years} anni\n` +
            "L'offerta è definitiva. Nome/valore/età verranno recuperati dopo (giro TM)."
        )) return;
        setBusy(true);
        try {
            await api.post("/auctions/bids", {
                auction_id: auction.id,
                tm_id: id,
                cash_amount: amt,
                contract_years: years,
            });
            setTmId(""); setAmount(""); setYears(2);
            onSent?.();
            alert("Offerta inviata sul giocatore Transfermarkt.");
        } catch (e) {
            alert(e.response?.data?.detail || "Errore nell'invio dell'offerta");
        } finally {
            setBusy(false);
        }
    };

    const accent = auction.is_ugly_auction ? "rgb(var(--tc-e8ff00))" : "rgb(var(--tc-ff7a1a))";
    return (
        <div className="op-card p-5 mb-6" style={{ borderLeft: `3px solid ${accent}` }} data-testid="tm-id-bid-card">
            <div className="op-label" style={{ color: accent }}>// OFFERTA SU GIOCATORE TRANSFERMARKT</div>
            <p className="op-mono text-[11px] text-t8f95a3 mt-2 leading-relaxed">
                Per i giocatori non presenti sul sito, ma presenti nel PESDB: inserisci l&apos;ID Transfermarkt e fai l&apos;offerta.
                Nome, valore e età verranno recuperati dopo l&apos;assegnazione col giro TM.
                {auction.is_ugly_auction ? " ⚠️ Verifica che sia under 22." : " Il giocatore entrerà in prima squadra."}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-4">
                <div>
                    <label className="op-mono text-[9px] uppercase text-t5c6370">ID Transfermarkt</label>
                    <input
                        type="number" value={tmId} onChange={(e) => setTmId(e.target.value)}
                        placeholder="es. 581678"
                        className="w-full mt-1 bg-t0e0f12 border border-t22252a px-2 py-2 text-tffffff op-mono text-sm"
                        data-testid="tm-bid-id-input"
                    />
                </div>
                <div>
                    <label className="op-mono text-[9px] uppercase text-t5c6370">Offerta (mln)</label>
                    <input
                        type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(sanitizeDecimalInput(e.target.value))}
                        placeholder="es. 5"
                        className="w-full mt-1 bg-t0e0f12 border border-t22252a px-2 py-2 text-tffffff op-mono text-sm"
                        data-testid="tm-bid-amount-input"
                    />
                </div>
                <div>
                    <label className="op-mono text-[9px] uppercase text-t5c6370">Anni contratto</label>
                    <select
                        value={years} onChange={(e) => setYears(parseInt(e.target.value, 10))}
                        className="w-full mt-1 bg-t0e0f12 border border-t22252a px-2 py-2 text-tffffff op-mono text-sm"
                        data-testid="tm-bid-years-select"
                    >
                        {[1, 2, 3, 4, 5].map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div className="flex items-end">
                    <button
                        className="op-btn op-btn-primary text-xs w-full" onClick={submit} disabled={busy}
                        data-testid="tm-bid-submit"
                    >
                        {busy ? "Invio…" : "Invia offerta TM"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function MyBidRow({ bid }) {
    return (
        <div className="op-card p-3 flex items-center gap-3" style={{ borderLeft: "3px solid rgb(var(--tc-2bb6ff))" }}>
            <div className="flex-1 min-w-0">
                <div className="text-tffffff text-sm font-semibold truncate">{bid.player_name}</div>
                <div className="op-mono text-xs text-t8f95a3">
                    {trPositionShort(bid.player_position)} · VG {bid.player_vg} · contratto {bid.contract_years} anni
                </div>
            </div>
            <div className="text-right">
                <div className="op-mono text-base text-t2bb6ff font-bold">{fmtEur(bid.cash_amount)}</div>
                <div className="op-mono text-[9px] text-t5c6370 uppercase">Busta sigillata</div>
            </div>
        </div>
    );
}

function BidModal({ auction, player, onClose, onDone }) {
    const minPrice = player.market_value || 0;
    const [amountMln, setAmountMln] = useState(
        String((minPrice / 1_000_000) || 1)
    );
    const [years, setYears] = useState(3);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async () => {
        setErr(null);
        const amount = Math.round((parseFloat(amountMln) || 0) * 1_000_000);
        if (amount < minPrice) {
            setErr(`L'offerta minima è ${fmtEur(minPrice)}`);
            return;
        }
        setBusy(true);
        try {
            await api.post("/auctions/bids", {
                auction_id: auction.id,
                player_pes_id: player.pes_id,
                cash_amount: amount,
                contract_years: years,
            });
            onDone?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur p-4"
            data-testid="bid-modal"
        >
            <div className="op-card max-w-md w-full p-5">
                <div className="op-label mb-2" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// BUSTA CHIUSA</div>
                <div className="op-heading text-2xl">{player.name}</div>
                <div className="op-mono text-xs text-t8f95a3 mb-3">
                    {trPositionShort(player.position)} · VG {player.overall} · {player.age} anni
                </div>
                <div className="op-mono text-xs text-tc9ccd3 mb-4">
                    Prezzo TM: <span className="text-t2bb6ff">{fmtEur(minPrice)}</span> <span className="text-t5c6370">· offerta minima</span>
                </div>
                <div className="op-card p-2 mb-4 text-[11px] text-tc9ccd3" data-testid="bid-tm-freshness">
                    Prezzo aggiornato al <span className="text-tffffff">{player.tm_value_date || "—"}</span>
                    {player.tm_refreshed_at && <> · controllato il <span className="text-tffffff">{new Date(player.tm_refreshed_at).toLocaleDateString("it-IT")}</span></>}.
                    <div className="mt-1 text-tff7a1a">
                        Il prezzo può essere cambiato: controllalo in tempo reale su{" "}
                        <a href={tmUrl(player)} target="_blank" rel="noopener noreferrer" className="underline text-t2bb6ff" data-testid="bid-tm-live-link">Transfermarkt</a>.
                        All&apos;apertura delle buste vale il prezzo aggiornato: se è salito, un&apos;offerta più bassa viene scartata.
                    </div>
                </div>
                <div className="op-card p-2 mb-4 text-xs text-tff7a1a border-tff7a1a/40" style={{ background: "rgba(255,122,26,0.05)" }}>
                    ⚠ Una volta inviata, l&apos;offerta è <strong>definitiva</strong>: non potrai modificarla o ritirarla.
                </div>

                <label className="op-label block mb-1">La tua offerta (mln €)</label>
                <input
                    type="text"
                    inputMode="decimal"
                    className="op-input mb-3"
                    value={amountMln}
                    onChange={(e) => setAmountMln(sanitizeDecimalInput(e.target.value))}
                    data-testid="bid-amount"
                />

                <label className="op-label block mb-1">Durata contratto (anni)</label>
                <select
                    className="op-input mb-4"
                    value={years}
                    onChange={(e) => setYears(parseInt(e.target.value, 10))}
                    data-testid="bid-years"
                >
                    {[1, 2, 3, 4, 5].map((y) => (
                        <option key={y} value={y}>{y} {y === 1 ? "anno" : "anni"}</option>
                    ))}
                </select>

                {err && (
                    <div className="op-card p-3 mb-3 text-sm text-tff4d4d border-tff4d4d/40">{err}</div>
                )}

                <div className="flex justify-end gap-2">
                    <button className="op-btn" onClick={onClose} disabled={busy} data-testid="bid-cancel">
                        Annulla
                    </button>
                    <button className="op-btn op-btn-primary" onClick={submit} disabled={busy} data-testid="bid-submit">
                        {busy ? "Invio…" : "Invia offerta definitiva"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function AuctionResults({ auction }) {
    const results = auction.results || [];
    const won = results.filter((r) => r.status === "won");
    const ties = results.filter((r) => r.status === "tie");

    return (
        <section className="mt-10" data-testid="auction-results">
            <div className="op-label mb-3" style={{ color: "rgb(var(--tc-e8ff00))" }}>// RISULTATI ASTA</div>
            <h2 className="op-heading text-3xl mb-5">{auction.name}</h2>

            {ties.length > 0 && (
                <div className="op-card p-4 mb-6" style={{ borderLeft: "3px solid rgb(var(--tc-ff7a1a))" }}>
                    <div className="op-label mb-1" style={{ color: "rgb(var(--tc-ff7a1a))" }}>
                        // PAREGGI DA RISOLVERE ({ties.length})
                    </div>
                    <p className="text-xs text-tc9ccd3">
                        L&apos;admin deve scegliere il vincitore tra le squadre in pareggio dal pannello CDP.
                    </p>
                </div>
            )}

            {won.length > 0 && (
                <div className="space-y-2 mb-6">
                    <div className="op-label mb-2">// ASSEGNATI ({won.length})</div>
                    {won.map((r) => (
                        <div key={r.player_pes_id} className="op-card p-3" data-testid={`won-${r.player_pes_id}`}>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-tffffff text-sm font-semibold">{r.player_name}</div>
                                    <div className="op-mono text-xs text-t8f95a3">
                                        Vincitore: <span className="text-tffffff">{r.winner_team_name}</span>
                                        {r.contract_years ? ` · ${r.contract_years} anni` : ""}
                                    </div>
                                </div>
                                <div className="op-mono text-base text-te8ff00 font-bold">{fmtEur(r.amount)}</div>
                            </div>
                            <BidsTimeline bids={r.all_bids} winnerTeam={r.winner_team_name} />
                        </div>
                    ))}
                </div>
            )}

            {ties.length > 0 && (
                <div className="space-y-2">
                    <div className="op-label mb-2" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// PAREGGI ({ties.length})</div>
                    {ties.map((r) => (
                        <div key={r.player_pes_id} className="op-card p-3" data-testid={`tie-${r.player_pes_id}`} style={{ borderLeft: "3px solid rgb(var(--tc-ff7a1a))" }}>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-tffffff text-sm font-semibold">{r.player_name}</div>
                                    <div className="op-mono text-xs text-t8f95a3">
                                        Tied: {(r.tied_team_names || []).join(", ")}
                                    </div>
                                </div>
                                <div className="op-mono text-base text-tff7a1a font-bold">{fmtEur(r.amount)}</div>
                            </div>
                            <BidsTimeline bids={r.all_bids} winnerTeam={null} />
                        </div>
                    ))}
                </div>
            )}

            {results.length === 0 && (
                <div className="op-card p-5 text-t8f95a3 text-sm">
                    Nessuna offerta ricevuta in questa asta.
                </div>
            )}
        </section>
    );
}
